/**
 * `ez.a.create_calendar_event` — *create an {event, eventMapping} in Google Calendar*
 * (static recipe `6001`, app `googleCalendar`, 1 automation).
 *
 * `eventMapping` is the same `dynamicMapping` structure as the cross-board creators', with
 * Google's five target fields instead of a board's columns — `summary`, `description`,
 * `location`, `start`, `end` (verbatim from `configurationFields.eventMapping` in
 * `recipe-catalogue-static.json`). It is resolved with the shared mapping renderer, so a
 * `{type:'field'}` part reads the item's cell text exactly as it does for a cross-board item.
 *
 * Google's `events.insert` needs `start`/`end`; an event whose `start` renders empty is skipped
 * rather than invented.
 */
import { z } from 'zod';
import { defineAction } from '../block.js';
import { AutomationNotConnected } from '../integrations.js';
import { itemMappingSchema, mappingSourceColumns, resolveItemMapping } from '../item-mapping.js';
export const createCalendarEventParams = z.object({
    eventMapping: itemMappingSchema,
    sourceBoardId: z.union([z.number(), z.string()]).nullable().optional(),
});
export const createCalendarEventAction = defineAction({
    id: 'ez.a.create_calendar_event',
    kind: 'action',
    app: 'googleCalendar',
    sentencePartial: 'create an {event, eventMapping} in Google Calendar, and sync future changes from this board',
    paramsSchema: createCalendarEventParams,
    executable: true,
    requiredApp: 'googleCalendar',
    assumed: true,
    references: (params) => [
        { kind: 'app', id: 'googleCalendar' },
        ...mappingSourceColumns(params.eventMapping).map((columnId) => ({ kind: 'column', id: columnId })),
    ],
    async execute(context, params) {
        const integrations = context.services.integrations;
        if (integrations === undefined || !(await integrations.isConnected('googleCalendar'))) {
            throw new AutomationNotConnected('googleCalendar');
        }
        const resolved = await resolveItemMapping(context, params.eventMapping);
        const field = (id) => resolved.cells.find((cell) => cell.columnId === id)?.text ?? '';
        const summary = resolved.name === null || resolved.name.length === 0 ? field('summary') : resolved.name;
        const start = field('start');
        const end = field('end');
        if (start.length === 0)
            return { created: false, reason: 'no_start' };
        const result = await integrations.createCalendarEvent({
            summary: summary.length === 0 ? 'Untitled' : summary,
            description: field('description'),
            location: field('location'),
            start,
            end: end.length === 0 ? start : end,
        });
        if (!result.ok)
            throw new Error(`calendar events.insert failed (${result.status}): ${result.detail ?? 'no detail'}`);
        return { created: true, eventId: result.externalId, summary, start, end, status: result.status };
    },
    retryable: (error) => /\((429|5\d\d)\)/.test(error instanceof Error ? error.message : ''),
});
//# sourceMappingURL=ez.a.create-calendar-event.js.map